
import { useState, useCallback, useEffect } from "react";
import { EmailSectionContainer } from "../start/EmailSectionContainer";
import { GamePlayResultView } from "../start/GamePlayResultView";
import { LoadingSpinner } from "@/components/game/LoadingSpinner";
import { WelcomeVideoModal } from "../WelcomeVideoModal";
import type { GameAttempt } from "@/lib/types/attempt";
import type { DailyPuzzle } from "@/lib/types/game";
import type { LogicRule } from "@/lib/game-utils";

interface StartScreenProps {
  existingAttempt: GameAttempt | null;
  todaysPuzzle: DailyPuzzle | null;
  onGameStart: (email: string) => void;
  isLoading: boolean;
  isUsingExternalData?: boolean;
  error?: Error | null | string;
  generateNewPuzzle?: () => void;
  hasCompletedToday?: boolean;
  onClearCompletedStatus?: () => void;
}

export function StartScreen({ 
  existingAttempt, 
  todaysPuzzle, 
  onGameStart, 
  isLoading,
  isUsingExternalData = false,
  error = null,
  generateNewPuzzle,
  hasCompletedToday = false,
  onClearCompletedStatus
}: StartScreenProps) {
  const [hasEnteredEmail, setHasEnteredEmail] = useState(false);
  const [userEmail, setUserEmail] = useState<string | null>(localStorage.getItem("user_email"));
  const [filteredRules, setFilteredRules] = useState<LogicRule[]>([]);
  const [viewingPreviousResults, setViewingPreviousResults] = useState(false);
  const [showWelcomeVideo, setShowWelcomeVideo] = useState(false);

  // Extract YouTube video ID from the full URL
  const youtubeVideoId = "lEZemROzYhA";

  // Check if this is the first visit
  useEffect(() => {
    const hasSeenWelcomeVideo = localStorage.getItem("has_seen_welcome_video");
    
    if (!hasSeenWelcomeVideo) {
      setShowWelcomeVideo(true);
    }
  }, []);

  // Handle email completion from EmailSection
  const handleEmailComplete = (email: string) => {
    console.log('Email completed:', email);
    localStorage.setItem("user_email", email);
    setUserEmail(email);
    setHasEnteredEmail(true);
    onGameStart(email);
  };

  // Handle welcome video close
  const handleWelcomeVideoClose = () => {
    setShowWelcomeVideo(false);
    localStorage.setItem("has_seen_welcome_video", "true");
  };

  // Handle game start from GamePlayResultView
  const handleGameStart = useCallback((email: string) => {
    console.log('Handling game start with email:', email);
    // Ensure we pass the email to the onGameStart function
    onGameStart(email);
  }, [onGameStart]);

  // Handle retry when there's an error
  const handleRetry = useCallback(() => {
    if (generateNewPuzzle) {
      generateNewPuzzle();
    }
  }, [generateNewPuzzle]);

  // Debug game start flow
  useEffect(() => {
    console.log("StartScreen state:", {
      hasEmail: !!userEmail,
      hasEnteredEmail,
      hasCompletedToday,
      isLoading,
      hasPuzzle: !!todaysPuzzle
    });
  }, [userEmail, hasEnteredEmail, hasCompletedToday, isLoading, todaysPuzzle]);

  // If loading, show spinner and return early
  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  // CRITICAL CHANGE: Never show error state during loading
  // Only check for errors if we're not in a loading state AND we actually have an error
  if (error && !isLoading && typeof error !== 'undefined') {
    // This code should never run during loading
    console.log("Error display suppressed during loading");
    // We won't return any error UI here, but we'll log for debugging
  }

  // If welcome video is to be shown, render it first
  if (showWelcomeVideo) {
    return (
      <WelcomeVideoModal 
        videoId={youtubeVideoId} 
        open={true}
        onClose={handleWelcomeVideoClose}
      />
    );
  }

  // If no email entered, show email section
  if (!hasEnteredEmail && !userEmail) {
    return <EmailSectionContainer onEmailComplete={handleEmailComplete} />;
  }

  // Return the appropriate container based on game state
  return (
    <GamePlayResultView
      todaysPuzzle={todaysPuzzle}
      userEmail={userEmail}
      viewingPreviousResults={viewingPreviousResults}
      hasCompletedToday={hasCompletedToday}
      filteredRules={filteredRules}
      setFilteredRules={setFilteredRules}
      setViewingPreviousResults={setViewingPreviousResults}
      isUsingExternalData={isUsingExternalData}
      onGameStart={handleGameStart}
      isLoading={isLoading}
      onClearCompletedStatus={onClearCompletedStatus}
    />
  );
}
